import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Car,
  Wrench,
  Package,
  Receipt,
  Users,
  UserCog,
  Truck,
  BarChart3,
  Bell,
  LogOut,
  Search,
  Menu,
  X,
  ScanLine,
  CalendarClock,
  ShieldCheck,
  Settings as SettingsIcon,
  UsersRound,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const LOGO = "https://customer-assets.emergentagent.com/job_db817c75-ae59-4832-b768-836f101e64ef/artifacts/g0jqvbfq_PHOTO-2022-03-31-12-08-17-1.jpg";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, test: "nav-dashboard", mod: "dashboard" },
  { to: "/showroom", label: "Showroom", icon: Car, test: "nav-showroom", mod: "showroom" },
  { to: "/leads", label: "Leads & CRM", icon: Users, test: "nav-leads", mod: "leads" },
  { to: "/service", label: "Service Shop", icon: Wrench, test: "nav-service", mod: "service" },
  { to: "/stores", label: "Stores", icon: Package, test: "nav-stores", mod: "stores" },
  { to: "/accounting", label: "Accounting", icon: Receipt, test: "nav-accounting", mod: "accounting" },
  { to: "/warranty", label: "Warranty", icon: ShieldCheck, test: "nav-warranty", mod: "warranty" },
  { to: "/customers", label: "Customers", icon: UserCog, test: "nav-customers", mod: "customers" },
  { to: "/vendors", label: "Vendors & PO", icon: Truck, test: "nav-vendors", mod: "vendors" },
  { to: "/employees", label: "Team & Bays", icon: Users, test: "nav-employees", mod: "employees" },
  { to: "/reports", label: "Reports", icon: BarChart3, test: "nav-reports", mod: "reports" },
  { to: "/users", label: "User Management", icon: UsersRound, test: "nav-users", adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, test: "nav-settings", mod: "settings" },
];

const MOBILE_NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, mod: "dashboard" },
  { to: "/showroom", label: "Cars", icon: Car, mod: "showroom" },
  { to: "/service", label: "Service", icon: Wrench, mod: "service" },
  { to: "/stores", label: "Stores", icon: Package, mod: "stores" },
  { to: "/accounting", label: "Bill", icon: Receipt, mod: "accounting" },
];

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === "admin" || user?.user_type === "admin";
  const canNav = (n) => {
    if (n.adminOnly) return isAdmin;
    if (!n.mod) return true;
    if (isAdmin) return true;
    return !!user?.permissions?.[n.mod]?.view;
  };

  return (
    <div className="min-h-screen flex bg-[var(--kk-bg)]">
      {/* Desktop side nav */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-[var(--kk-border)] bg-[var(--kk-surface)] sticky top-0 h-screen">
        <div className="p-6 border-b border-[var(--kk-border)] flex items-center gap-3">
          <img src={LOGO} alt="Karr Kraft" className="w-16 h-16 object-contain" data-testid="sidebar-logo" />
          <div>
            <div className="font-display text-lg leading-none">KARR KRAFT</div>
            <div className="kk-overline text-[0.6rem] mt-1">Command Center</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.filter(canNav).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.test}
              className={({ isActive }) =>
                "kk-nav-link " + (isActive ? "kk-nav-link-active" : "")
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[var(--kk-border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--kk-black)] text-white flex items-center justify-center font-display">
              {user?.name?.[0] || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="kk-overline text-[0.55rem]">{user?.role}</div>
            </div>
            <button
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              data-testid="logout-btn"
              className="hover:text-[var(--kk-black)] text-[var(--kk-muted)]"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[var(--kk-bg)]/80 backdrop-blur-md border-b border-[var(--kk-border)]">
          <div className="flex items-center gap-3 px-4 lg:px-8 py-3">
            <button
              className="lg:hidden"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <img src={LOGO} alt="" className="lg:hidden w-12 h-12 object-contain" />
            <div className="flex-1 max-w-md hidden md:flex items-center gap-2 border-b border-transparent hover:border-[var(--kk-border)]">
              <Search size={14} className="text-[var(--kk-muted)]" />
              <input
                placeholder="Search cars, customers, job cards…"
                className="kk-input py-1 text-sm border-0"
                data-testid="global-search"
              />
            </div>
            <div className="flex-1 md:flex-none" />
            <button
              onClick={() => navigate("/stores?scan=1")}
              className="kk-pill kk-pill-black hidden sm:inline-flex"
              data-testid="top-scan-btn"
            >
              <ScanLine size={12} /> Scan QR
            </button>
            <button className="relative" data-testid="notif-btn">
              <Bell size={18} />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[var(--kk-lime)] rounded-full" />
            </button>
            <span className="kk-pill kk-pill-lime hidden sm:inline-flex">{user?.role}</span>
          </div>
        </header>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden border-b border-[var(--kk-border)] bg-[var(--kk-surface)] kk-fade-in">
            {NAV.filter(canNav).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  "kk-nav-link " + (isActive ? "kk-nav-link-active" : "")
                }
              >
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
            <button
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="kk-nav-link w-full text-left"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        )}

        <main className="flex-1 pb-24 lg:pb-8">{children}</main>

        {/* Mobile bottom tabs */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-[var(--kk-surface)] border-t border-[var(--kk-border)] grid grid-cols-5 z-40">
          {MOBILE_NAV.filter(canNav).map((n) => {
            const active = loc.pathname.startsWith(n.to);
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-${n.label.toLowerCase()}`}
                className={
                  "relative flex flex-col items-center justify-center py-2.5 text-[0.6rem] uppercase tracking-wider " +
                  (active ? "text-[var(--kk-black)] font-bold" : "text-[var(--kk-muted)]")
                }
              >
                <n.icon size={18} />
                <span className="mt-0.5">{n.label}</span>
                {active && (
                  <span className="absolute top-0 w-10 h-0.5 bg-[var(--kk-lime)]" />
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
